/**
 * Types shared between the Express API and the React client.
 * Keeping these in one place means a route's response shape and the
 * component that renders it can never quietly drift apart.
 */
export * from './holidays.js';
export * from './district.js';
export * from './jobs.js';
export * from './photos.js';
export * from './documents.js';
export * from './emoji.js';
export * from './sticky.js';
export * from './nav.js';
export * from './noteFolders.js';
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
export const HEADER_STYLES = ['plain', 'filled'];
export const HEADER_STYLE_LABELS = {
    plain: 'Pale, with dark writing',
    filled: 'Filled with colour, with light writing',
};
/**
 * Which of the theme's colours a page header wears.
 *
 * These are names of colours in the palette, not colours — so a header set to
 * `mauve` is mauve in every theme, in *that theme's* mauve. Choosing a hex
 * would have pinned one theme's idea of it onto the other nine.
 *
 * Null, the default, means each page keeps its own: the calendar mauve, the
 * roster rust. Everything here is an accent; the three papers and the three
 * inks are not on the list, because a header the colour of the page is not a
 * header and one the colour of the writing is a black bar.
 */
export const HEADER_COLORS = [
    'terracotta',
    'rust',
    'clay',
    'ochre',
    'olive',
    'sage',
    'rose',
    'mauve',
    'walnut',
    'espresso',
];
export const WEEKDAY_LABELS = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
};
/*
 * The .js extension is required, not optional. This package emits real ESM,
 * and Node's resolver does not guess extensions — TypeScript copies the
 * specifier through untouched, so a bare './timezones' typechecks cleanly and
 * then fails at startup in production with ERR_MODULE_NOT_FOUND.
 */
export * from './timezones.js';
export * from './weather.js';
export * from './subPlans.js';
export * from './subPlanSeed.js';
export * from './worksheetLayout.js';
export const GRADE_LEVELS = [
    'K',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
];
export function gradeLabel(grade) {
    return grade === 'K' ? 'Kindergarten' : `Grade ${grade}`;
}
export const STANDARD_SUBJECT_LABELS = {
    ela: 'English/Language Arts',
    math: 'Mathematics',
    science: 'Science',
    social: 'Social Studies',
};
/** Returns the weekday key for a date, clamping weekends to Monday. */
export function weekdayKeyFor(date) {
    const day = date.getDay();
    if (day === 0 || day === 6)
        return 'mon';
    return WEEKDAYS[day - 1];
}
/** Formats a date as 'YYYY-MM-DD' using local calendar values. */
export function toDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** The Monday of the week containing `date`, at local midnight. */
export function startOfWeek(date = new Date()) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    return d;
}
export function weekKeyOf(date = new Date()) {
    return toDateKey(startOfWeek(date));
}
export function addWeeks(key, delta) {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + delta * 7);
    return toDateKey(date);
}
/** The calendar date a given weekday falls on within a week. */
export function dateOfWeekday(week, weekday) {
    const [y, m, d] = week.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + WEEKDAYS.indexOf(weekday));
    return date;
}
/** "Aug 3 – 7" style label for a week. */
export function weekRangeLabel(week) {
    const mon = dateOfWeekday(week, 'mon');
    const fri = dateOfWeekday(week, 'fri');
    const monthDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
    const dayOnly = new Intl.DateTimeFormat(undefined, { day: 'numeric' });
    return mon.getMonth() === fri.getMonth()
        ? `${monthDay.format(mon)} – ${dayOnly.format(fri)}`
        : `${monthDay.format(mon)} – ${monthDay.format(fri)}`;
}
/* ---------------------------------------------------------------- to-dos */
export const PRIORITIES = ['low', 'normal', 'high'];
/** 'once' belongs to a single week. 'weekly' repeats until you stop it. */
export const TODO_KINDS = ['once', 'weekly'];
/** Deleting a repeating item: just this week, or the whole series. */
export const DELETE_SCOPES = ['week', 'series'];
/* ------------------------------------------------------------- bookmarks */
export const ICON_TYPES = ['text', 'emoji', 'image'];
/** Image rules, shared so the client can pre-validate before uploading. */
export const IMAGE_RULES = {
    /** Rejected outright: SVG can carry script and would be served same-origin. */
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
    maxUploadBytes: 2 * 1024 * 1024,
    maxStoredBytes: 512 * 1024,
    bookmarkIconPx: 128,
    /** Larger than the roster thumbnail so the profile view stays sharp. */
    studentPhotoPx: 320,
    /**
     * A page background is stretched across a whole monitor, so it needs far
     * more pixels than an icon and gets its own size budget.
     */
    backgroundPx: 1800,
    maxBackgroundUploadBytes: 8 * 1024 * 1024,
    maxStoredBackgroundBytes: 1_500_000,
};
/** As many as one teacher may keep. Generous, but not a photo library. */
export const MAX_CUSTOM_BACKGROUNDS = 24;
/**
 * The type-size steps offered in Settings.
 *
 * Named steps rather than a free slider: a teacher wants "a bit bigger", not
 * 1.17, and a bounded list means no one can shrink the app to the point of
 * being unable to find the control that shrank it.
 */
export const TEXT_SCALES = [
    { value: 0.85, label: 'Compact' },
    { value: 0.92, label: 'Small' },
    { value: 1, label: 'Standard' },
    { value: 1.1, label: 'Comfortable' },
    { value: 1.2, label: 'Large' },
    { value: 1.35, label: 'Larger' },
    { value: 1.5, label: 'Largest' },
];
export const HEADING_SCALES = [
    { value: 0.85, label: 'Understated' },
    { value: 1, label: 'Standard' },
    { value: 1.15, label: 'Bolder' },
    { value: 1.3, label: 'Big' },
    { value: 1.5, label: 'Biggest' },
];
/** Keeps a stored number inside the offered range, whatever else arrives. */
export function clampScale(value, min = 0.7, max = 1.6) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return 1;
    return Math.min(Math.max(value, min), max);
}
/**
 * "2025-2026" from a pair of dates.
 *
 * Named for the two calendar years it spans, which is how every school
 * refers to it. A year ending in the same calendar year it started — a
 * summer programme, say — is named once.
 */
export function schoolYearLabel(start, end) {
    const endYear = Number(end.slice(0, 4));
    const startYear = start ? Number(start.slice(0, 4)) : endYear - 1;
    return startYear === endYear ? String(endYear) : `${startYear}-${endYear}`;
}
/** What archiving touches, spelled out for the warning in Settings. */
export const ARCHIVED_BY_ROLLOVER = [
    'Your student roster — every child, with their notes and flags',
    'The seating chart, emptied of students (the furniture stays exactly as it is)',
    'Assessments and every score in them',
];
export const KEPT_BY_ROLLOVER = [
    'Lesson plans, and the schedule they sit on',
    'The calendar and everything in it',
    'Notes and their folders',
    'To-dos, habits and bookmarks',
    'Every setting, including your theme and background',
];
export const MAIL_LIMITS = {
    /** Per fetch. Enough to scroll a morning's post without stalling. */
    pageSize: 25,
    maxPageSize: 50,
    subject: 300,
    body: 100_000,
};
/**
 * Gmail's own servers. Hard-coded rather than asked for, because the teacher
 * knows her email address and would have to look these up.
 */
export const GMAIL_SERVERS = {
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 465,
};
/** Folder colours, drawn from the same palette as everything else. */
export const NOTE_FOLDER_COLORS = [
    { value: '#B07C63', label: 'Terracotta' },
    { value: '#9A6045', label: 'Rust' },
    { value: '#D9A85C', label: 'Ochre' },
    { value: '#7C8A5F', label: 'Olive' },
    { value: '#A3B18A', label: 'Sage' },
    { value: '#8E9AAF', label: 'Slate' },
    { value: '#9C7A93', label: 'Mauve' },
    { value: '#C08B8B', label: 'Rose' },
    { value: '#6B5D52', label: 'Walnut' },
];
export const DEFAULT_NOTE_FOLDER_COLOR = '#B07C63';
export const NOTE_LIMITS = {
    title: 120,
    /** Long enough for a page of meeting minutes with a picture or two. */
    body: 200_000,
    folderName: 40,
    maxFolders: 40,
    /** Pixels on the long edge. Wide enough to read a photographed handout. */
    imagePx: 1400,
    maxImageUploadBytes: 8 * 1024 * 1024,
    maxStoredImageBytes: 900_000,
};
/** Settings store 'custom:<id>' for an uploaded image. */
export const CUSTOM_BACKGROUND_PREFIX = 'custom:';
export function customBackgroundId(value) {
    return value?.startsWith(CUSTOM_BACKGROUND_PREFIX)
        ? value.slice(CUSTOM_BACKGROUND_PREFIX.length)
        : null;
}
/* ---------------------------------------------------------------- events */
/**
 * Events are stored as wall-clock values — a calendar date plus minutes from
 * midnight — not as UTC instants.
 *
 * This is deliberate. A classroom calendar means "9:00 AM on the 12th" and
 * should read that way no matter what timezone the server runs in or whether
 * a clock has shifted for daylight saving. Storing instants would make the
 * displayed time depend on where the container happens to be hosted.
 */
export const EVENT_CATEGORIES = [
    'school',
    'meeting',
    'personal',
    'duty',
    'holiday',
    'reminder',
];
export const CATEGORY_COLORS = {
    school: '#B07C63',
    meeting: '#9C7A93',
    personal: '#A3B18A',
    duty: '#D9A85C',
    holiday: '#C08B8B',
    reminder: '#C4A287',
};
export const BUILTIN_CATEGORIES = EVENT_CATEGORIES.map((k) => ({
    key: k,
    label: k.charAt(0).toUpperCase() + k.slice(1),
    color: CATEGORY_COLORS[k],
    builtin: true,
    hidden: false,
}));
/** Turns a display name into a stable key. */
export function slugify(name) {
    return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);
}
/** Palette for overriding an event's colour independently of its category. */
export const EVENT_COLORS = [
    '#B07C63', // terracotta
    '#9A6045', // rust
    '#C4A287', // clay
    '#D9A85C', // ochre
    '#A3B18A', // sage
    '#7C8A5F', // olive
    '#9C7A93', // mauve
    '#C08B8B', // dusty rose
    '#8E9AAF', // slate blue
    '#6B5D52', // walnut
];
/**
 * An event's colour: its own override if set, otherwise its category's.
 * Pass the resolved category list to pick up custom categories.
 */
export function eventColor(e, categories) {
    if (e.color)
        return e.color;
    const found = categories?.find((c) => c.key === e.category);
    return found?.color ?? CATEGORY_COLORS[e.category] ?? '#C4A287';
}
export function categoryLabel(key, categories) {
    const found = categories?.find((c) => c.key === key);
    if (found)
        return found.label;
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/-/g, ' ');
}
export const RECUR_FREQS = ['none', 'daily', 'weekly', 'monthly'];
export const CALENDAR_VIEWS = ['day', 'week', 'month'];
/* ------------------------------------------------------- date arithmetic */
export function parseDateKey(key) {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
}
export function addDays(key, days) {
    const d = parseDateKey(key);
    d.setDate(d.getDate() + days);
    return toDateKey(d);
}
export function addMonths(key, months) {
    const d = parseDateKey(key);
    const targetMonth = d.getMonth() + months;
    const anchor = new Date(d.getFullYear(), targetMonth, 1);
    // Clamp so Jan 31 + 1 month lands on Feb 28, not Mar 3.
    const lastDay = daysInMonth(anchor.getFullYear(), anchor.getMonth() + 1);
    anchor.setDate(Math.min(d.getDate(), lastDay));
    return toDateKey(anchor);
}
export function daysInMonth(year, month1to12) {
    return new Date(year, month1to12, 0).getDate();
}
/**
 * Compact range for calendar chips: "9–10am" when both sides share a
 * meridiem, "11am–1pm" when they don't.
 */
export function minutesRangeLabel(start, end) {
    const sameMeridiem = start < 720 === end < 720;
    if (!sameMeridiem)
        return `${minutesToLabel(start)}–${minutesToLabel(end)}`;
    const left = minutesToLabel(start).replace(/(am|pm)$/, '');
    return `${left}–${minutesToLabel(end)}`;
}
export function minutesToLabel(minutes) {
    const h24 = Math.floor(minutes / 60);
    const m = minutes % 60;
    const suffix = h24 < 12 ? 'am' : 'pm';
    const h = h24 % 12 === 0 ? 12 : h24 % 12;
    return m === 0 ? `${h}${suffix}` : `${h}:${String(m).padStart(2, '0')}${suffix}`;
}
/**
 * Expands a stored event into the concrete dates it falls on within a range.
 * Shared so the calendar grid and the dashboard resolve occurrences the same
 * way rather than each inventing their own rules.
 */
export function expandEvent(event, from, to) {
    const { recurrence: r } = event;
    const skip = new Set(event.exceptions);
    const instance = (date, isRecurring) => ({
        id: `${event.id}:${date}`,
        seriesId: event.id,
        title: event.title,
        description: event.description,
        location: event.location,
        date,
        allDay: event.allDay,
        startMinutes: event.startMinutes,
        endMinutes: event.endMinutes,
        category: event.category,
        color: event.color,
        isRecurring,
    });
    if (!r || r.freq === 'none') {
        if (event.date >= from && event.date <= to && !skip.has(event.date)) {
            return [instance(event.date, false)];
        }
        return [];
    }
    const interval = Math.max(1, r.interval || 1);
    const limit = r.until && r.until < to ? r.until : to;
    const out = [];
    let cursor = event.date;
    let guard = 0;
    // Fast-forward to the range instead of stepping one interval at a time
    // from a start date that might be years back.
    if (cursor < from) {
        if (r.freq === 'daily') {
            const gap = Math.floor((parseDateKey(from).getTime() - parseDateKey(cursor).getTime()) / 86_400_000);
            cursor = addDays(cursor, Math.floor(gap / interval) * interval);
        }
        else if (r.freq === 'weekly') {
            const gapWeeks = Math.floor((parseDateKey(from).getTime() - parseDateKey(cursor).getTime()) / (7 * 86_400_000));
            cursor = addDays(cursor, Math.floor(gapWeeks / interval) * interval * 7);
        }
    }
    while (cursor <= limit && guard++ < 1200) {
        if (cursor >= from && cursor >= event.date && !skip.has(cursor)) {
            out.push(instance(cursor, true));
        }
        cursor =
            r.freq === 'daily'
                ? addDays(cursor, interval)
                : r.freq === 'weekly'
                    ? addDays(cursor, interval * 7)
                    : addMonths(cursor, interval);
    }
    return out;
}
/* ---------------------------------------------------------- lesson plans */
/** The fields captured for one subject on one day. */
export const PLAN_FIELDS = [
    'objective',
    'standards',
    'activities',
    'materials',
    'assessment',
    'notes',
];
export const PLAN_FIELD_LABELS = {
    objective: 'Objective',
    standards: 'Standards',
    activities: 'Activities',
    materials: 'Materials',
    assessment: 'Assessment',
    notes: 'Notes',
};
/**
 * Cell values hold a small, sanitised subset of HTML so a teacher can bold,
 * italicise, underline, highlight and make lists. The server re-sanitises
 * everything on write — see server/src/services/sanitizeHtml.ts — so nothing
 * here is trusted.
 */
/**
 * How much structure one lesson wants.
 *
 * `simple` is one block of writing and nothing else — which is what most days
 * actually are, and what the six labelled boxes were making into a form to
 * fill in. `detailed` is those six boxes, for the lesson that genuinely has an
 * objective and a standard and an exit ticket.
 *
 * Per cell rather than per week or per app, because both are true on the same
 * Tuesday: the reading block is planned properly and the bathroom break is a
 * line of text.
 */
export const PLAN_CELL_MODES = ['simple', 'detailed'];
/**
 * Which editor a cell opens in.
 *
 * Simple is the default for anything new, because it is what most lessons
 * need. But a cell written before this existed has no `mode` and six filled-in
 * fields, and opening that in simple mode would show a blank page over a
 * year's planning. So an unmarked cell with detailed content is detailed —
 * nothing already written changes shape.
 */
export function cellMode(cell) {
    if (cell?.mode)
        return cell.mode;
    if (cell && PLAN_FIELDS.some((field) => stripHtml(cell[field])))
        return 'detailed';
    return 'simple';
}
/** The only highlight colours the sanitiser will keep. */
export const HIGHLIGHT_COLORS = [
    { name: 'Yellow', value: '#FBEFC8' },
    { name: 'Green', value: '#DCE7CE' },
    { name: 'Blue', value: '#D6E2EC' },
    { name: 'Pink', value: '#F2DCDC' },
    { name: 'Peach', value: '#F6E0CD' },
];
/** Plain text from stored markup, for previews and character counts. */
export function stripHtml(html) {
    if (!html)
        return '';
    return html
        .replace(/<li>/gi, '• ')
        .replace(/<\/(p|div|li|ul|ol)>/gi, ' ')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim();
}
/** The starting schedule for a teacher who hasn't set one up yet. */
export const DEFAULT_PLAN_ROWS = [
    { label: 'Morning Meeting', timeStart: '8:15', timeEnd: '8:45', sortOrder: 0, noStudents: false },
    { label: 'Reading', timeStart: '8:45', timeEnd: '9:45', sortOrder: 1, noStudents: false },
    { label: 'Writing', timeStart: '9:45', timeEnd: '10:30', sortOrder: 2, noStudents: false },
    // Specials and lunch are on the schedule but aren't your instructional time.
    { label: 'Specials', timeStart: '10:30', timeEnd: '11:15', sortOrder: 3, noStudents: true },
    { label: 'Math', timeStart: '11:15', timeEnd: '12:15', sortOrder: 4, noStudents: false },
    { label: 'Lunch / Recess', timeStart: '12:15', timeEnd: '1:00', sortOrder: 5, noStudents: true },
    {
        label: 'Science / Social Studies',
        timeStart: '1:00',
        timeEnd: '1:45',
        sortOrder: 6,
        noStudents: false,
    },
    { label: 'Intervention', timeStart: '1:45', timeEnd: '2:30', sortOrder: 7, noStudents: false },
];
/**
 * True when the cell has anything worth showing, ignoring empty markup.
 *
 * Both halves count. A cell written in simple mode has nothing in the six
 * fields and would otherwise read as empty — which is how a whole week of
 * planning would come to show a grid of plus signs.
 */
export function cellIsEmpty(cell) {
    if (!cell)
        return true;
    if (stripHtml(cell.plan))
        return false;
    return PLAN_FIELDS.every((f) => !stripHtml(cell[f]));
}
/** A one-line plain-text summary of a cell. */
export function cellPreview(cell) {
    if (!cell)
        return '';
    if (cellMode(cell) === 'simple')
        return stripHtml(cell.plan);
    for (const field of ['objective', 'activities', 'notes']) {
        const text = stripHtml(cell[field]);
        if (text)
            return text;
    }
    return '';
}
/**
 * The Standards cell stores markup so it prints and previews like every other
 * field, but the editor works with structured entries. These two functions are
 * the bridge, and they round-trip: parse(serialize(x)) === x.
 *
 * Anything that doesn't match the picked-standard shape is preserved as a
 * codeless entry, so text typed before this existed is never dropped.
 */
export function serializeStandards(items) {
    return items
        .map((i) => (i.code ? `<b>${i.code}</b> ${i.text}` : i.text))
        .filter((s) => s.trim())
        .join('<br />');
}
/**
 * Just the codes — "1.CA.1, 1.RF.4.1" — for the weekly table, where the full
 * wording of a standard would swamp the lesson itself. Entries the teacher
 * typed freehand have no code, so their text stands in.
 */
export function standardCodesLabel(html) {
    return parseStandardsHtml(html)
        .map((s) => s.code ?? s.text)
        .join(', ');
}
export function parseStandardsHtml(html) {
    if (!html?.trim())
        return [];
    return html
        .split(/<br\s*\/?>/i)
        .map((segment) => {
        const match = segment.match(/^\s*<b>(.*?)<\/b>\s*([\s\S]*)$/i);
        if (match) {
            return { code: stripHtml(match[1]), text: stripHtml(match[2]) };
        }
        const text = stripHtml(segment);
        return text ? { code: null, text } : null;
    })
        .filter((x) => x !== null && x.text.length + (x.code?.length ?? 0) > 0);
}
/** The first non-empty field's markup, for previews that render formatting. */
export function cellPreviewHtml(cell) {
    if (!cell)
        return '';
    if (cellMode(cell) === 'simple')
        return stripHtml(cell.plan) ? (cell.plan ?? '') : '';
    for (const field of ['objective', 'activities', 'notes']) {
        if (stripHtml(cell[field]))
            return cell[field] ?? '';
    }
    return '';
}
export const HABIT_COLORS = [
    '#B07C63',
    '#A3B18A',
    '#9C7A93',
    '#D9A85C',
    '#C08B8B',
    '#C4A287',
];
export const BUILTIN_STUDENT_FLAGS = [
    { key: 'iep', label: 'IEP', short: 'IEP', color: '#9C7A93', builtin: true, hidden: false },
    { key: 'plan504', label: '504', short: '504', color: '#8E9AAF', builtin: true, hidden: false },
    { key: 'ell', label: 'ELL', short: 'ELL', color: '#A3B18A', builtin: true, hidden: false },
    {
        key: 'highAbility',
        label: 'High ability',
        short: 'HA',
        color: '#7C8A5F',
        builtin: true,
        hidden: false,
    },
    {
        key: 'behavior',
        label: 'Behavior',
        short: 'BEH',
        color: '#C08B8B',
        builtin: true,
        hidden: false,
    },
    {
        key: 'allergy',
        label: 'Allergy',
        short: 'ALG',
        color: '#9A6045',
        builtin: true,
        hidden: false,
    },
    {
        key: 'medical',
        label: 'Medical',
        short: 'MED',
        color: '#B07C63',
        builtin: true,
        hidden: false,
    },
    { key: 'speech', label: 'Speech', short: 'SPCH', color: '#C4A287', builtin: true, hidden: false },
    {
        key: 'intervention',
        label: 'Intervention',
        short: 'INT',
        color: '#D9A85C',
        builtin: true,
        hidden: false,
    },
];
export function flagDef(key, defs) {
    return (defs?.find((f) => f.key === key) ?? BUILTIN_STUDENT_FLAGS.find((f) => f.key === key) ?? null);
}
export function flagLabel(key, defs) {
    return flagDef(key, defs)?.label ?? key;
}
/**
 * Badge text. Falls back to the first four characters so a flag deleted from
 * under a student still renders as something readable.
 */
export function flagShort(key, defs) {
    return flagDef(key, defs)?.short ?? key.slice(0, 4).toUpperCase();
}
export function flagColor(key, defs) {
    return flagDef(key, defs)?.color ?? '#C4A287';
}
/** Auto-generated badge text when adding a flag: "Small group" -> "SMGR". */
export function suggestShort(label) {
    const words = label.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return '';
    if (words.length === 1)
        return words[0].slice(0, 4).toUpperCase();
    return words
        .slice(0, 2)
        .map((w) => w.slice(0, 2))
        .join('')
        .toUpperCase();
}
export const NOTE_TYPES = [
    'behavior',
    'academic',
    'contact',
    'positive',
    'medical',
    'general',
];
export const NOTE_TYPE_LABELS = {
    behavior: 'Behavior',
    academic: 'Academic',
    contact: 'Parent contact',
    positive: 'Positive',
    medical: 'Health',
    general: 'General',
};
/** What they go by, falling back to their first name. */
export function studentName(s) {
    return s.preferredName?.trim() || s.firstName;
}
export function studentFullName(s) {
    return `${studentName(s)} ${s.lastName}`.trim();
}
/** "Ruiz, Ana" — how a roster and a gradebook are ordered. */
export function studentSortName(s) {
    return `${s.lastName}, ${s.firstName}`;
}
export function studentInitials(s) {
    const first = studentName(s).trim()[0] ?? '';
    const last = s.lastName.trim()[0] ?? '';
    return `${first}${last}`.toUpperCase();
}
/**
 * Alphabetical by last name, then first — the order class numbers follow.
 *
 * Compared with `localeCompare` so that accented names sort where a teacher
 * would look for them (Ávila before Baker) rather than after Z.
 */
export function compareStudents(a, b) {
    const byLast = a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' });
    if (byLast !== 0)
        return byLast;
    return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' });
}
/**
 * The numbers the class *would* have if renumbered right now, keyed by id.
 *
 * Kept as a pure function so the roster can show "3 students would be
 * renumbered" before anything is written.
 */
export function alphabeticalNumbers(students) {
    const out = {};
    [...students].sort(compareStudents).forEach((s, i) => {
        out[s.id] = i + 1;
    });
    return out;
}
/** Ids whose stored number differs from the alphabetical one. */
export function misnumberedStudents(students) {
    const inClass = students.filter((s) => s.active);
    const fresh = alphabeticalNumbers(inClass);
    return inClass.filter((s) => fresh[s.id] !== s.number).map((s) => s.id);
}
/**
 * The student in class already holding a number, if any.
 *
 * Archived students never hold one — their number is released when they are
 * archived — so only the current class can block a reassignment.
 */
export function numberHolder(students, number, exceptId) {
    return students.find((s) => s.active && s.number === number && s.id !== exceptId) ?? null;
}
/** Lowest number nobody in class holds — what a new or returning student gets. */
export function nextFreeNumber(students) {
    const taken = new Set(students.filter((s) => s.active && s.number !== null).map((s) => s.number));
    let n = 1;
    while (taken.has(n))
        n += 1;
    return n;
}
export function noteNeedsAttention(n) {
    return n.needsFollowUp && !n.followedUpAt;
}
/* ---------------------------------------------------------- seating chart */
/**
 * The room is a plain rectangle in its own coordinate space, and every item
 * is placed in those units rather than screen pixels. The canvas scales the
 * whole room to whatever space it has, so a chart drawn on a laptop looks the
 * same on a projector and prints without re-laying anything out.
 */
export const ROOM = {
    width: 1200,
    height: 960,
    /** Positions snap to this, which is what keeps rows looking like rows. */
    grid: 10,
    /** How wide the labelled wall band is. */
    wall: 34,
};
/**
 * Room units per foot.
 *
 * Everything is stored in abstract units, but a classroom is a real room with
 * real measurements — so one foot is forty units, which makes a standard
 * 30 × 24 ft room exactly the 1200 × 960 default and a 90-unit desk a
 * believable 27 inches wide.
 */
export const UNITS_PER_FOOT = 40;
export const DEFAULT_ROOM_FEET = { width: 30, height: 24 };
/** Rooms smaller than a large closet or bigger than a gym are a typo. */
export const ROOM_FEET_LIMITS = { min: 10, max: 60 };
export function feetToUnits(feet) {
    return Math.round(feet * UNITS_PER_FOOT);
}
export function roomUnits(widthFt, heightFt) {
    return { width: feetToUnits(widthFt), height: feetToUnits(heightFt) };
}
export function clampRoomFeet(value) {
    if (!Number.isFinite(value))
        return DEFAULT_ROOM_FEET.width;
    return Math.min(Math.max(Math.round(value), ROOM_FEET_LIMITS.min), ROOM_FEET_LIMITS.max);
}
/** "30 × 24 ft · 720 sq ft" */
export function roomSizeLabel(widthFt, heightFt) {
    return `${widthFt} × ${heightFt} ft · ${widthFt * heightFt} sq ft`;
}
/**
 * Square feet per child actually in a seat.
 *
 * Indiana sets no per-pupil floor area for general classrooms, so this is a
 * rule of thumb rather than a standard — around 30 sq ft per child is the
 * figure most elementary planning guides use. It is here to answer "is this
 * room going to feel tight?", not to certify anything.
 */
export function squareFeetPerStudent(widthFt, heightFt, students) {
    if (students <= 0)
        return null;
    return Math.round((widthFt * heightFt) / students);
}
export const ROOM_ITEM_KINDS = [
    // seating
    'desk',
    'deskRound',
    'deskTrapezoid',
    'table',
    'roundTable',
    'kidneyTable',
    'horseshoeTable',
    'beanbag',
    'floorSpot',
    'stool',
    'chair',
    'smallGroup',
    // fixtures
    'door',
    'window',
    'whiteboard',
    'smartboard',
    'easel',
    'cubbies',
    'coatHooks',
    'sink',
    'counter',
    'clock',
    'wordWall',
    // furniture
    'teacherDesk',
    'teacherChair',
    'rockingChair',
    'rug',
    'roundRug',
    'shelf',
    'cabinet',
    'storageBins',
    'computer',
    'printer',
    'cart',
    'lamp',
    'plant',
    'fishTank',
    'calmCorner',
    'mailboxes',
    'label',
];
/**
 * How an item is drawn.
 *
 * A kidney table drawn as a rectangle is just a rectangle, and a teacher
 * looking at their own room should recognise it at a glance — so the shapes
 * are real shapes rather than colour-coded boxes.
 */
export const ITEM_SHAPES = [
    'rect',
    'rounded',
    'circle',
    'kidney',
    'trapezoid',
    'horseshoe',
    'chair',
    'triangle',
    'blob',
];
export const ROOM_ITEM_SPECS = {
    /* ------------------------------------------------------------- seating */
    desk: {
        label: 'Desk',
        w: 90,
        h: 60,
        color: '#C4A287',
        seats: true,
        shape: 'rounded',
        group: 'seating',
    },
    deskRound: {
        label: 'Round desk',
        w: 80,
        h: 80,
        color: '#C4A287',
        seats: true,
        shape: 'circle',
        group: 'seating',
    },
    deskTrapezoid: {
        label: 'Trapezoid desk',
        w: 110,
        h: 60,
        color: '#C4A287',
        seats: true,
        shape: 'trapezoid',
        group: 'seating',
    },
    table: {
        label: 'Table',
        w: 180,
        h: 90,
        color: '#C4A287',
        seats: true,
        shape: 'rounded',
        group: 'seating',
    },
    roundTable: {
        label: 'Round table',
        w: 140,
        h: 140,
        color: '#C4A287',
        seats: true,
        shape: 'circle',
        group: 'seating',
    },
    kidneyTable: {
        label: 'Kidney table',
        w: 220,
        h: 130,
        color: '#B07C63',
        seats: true,
        shape: 'kidney',
        group: 'seating',
    },
    horseshoeTable: {
        label: 'Horseshoe table',
        w: 220,
        h: 150,
        color: '#B07C63',
        seats: true,
        shape: 'horseshoe',
        group: 'seating',
    },
    beanbag: {
        label: 'Beanbag',
        w: 80,
        h: 70,
        color: '#C08B8B',
        seats: true,
        shape: 'blob',
        group: 'seating',
    },
    floorSpot: {
        label: 'Carpet spot',
        w: 50,
        h: 50,
        color: '#A3B18A',
        seats: true,
        shape: 'circle',
        group: 'seating',
    },
    stool: {
        label: 'Stool',
        w: 45,
        h: 45,
        color: '#9C9086',
        seats: true,
        shape: 'circle',
        group: 'seating',
    },
    chair: {
        label: 'Chair',
        w: 45,
        h: 45,
        color: '#9C9086',
        seats: true,
        shape: 'chair',
        group: 'seating',
    },
    smallGroup: {
        label: 'Small group table',
        w: 200,
        h: 120,
        color: '#B07C63',
        seats: false,
        shape: 'rounded',
        group: 'seating',
    },
    /* ------------------------------------------------------------ fixtures */
    door: {
        label: 'Door',
        w: 90,
        h: 20,
        color: '#6B5D52',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    window: {
        label: 'Window',
        w: 160,
        h: 16,
        color: '#8E9AAF',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    whiteboard: {
        label: 'Whiteboard',
        w: 260,
        h: 18,
        color: '#9C9086',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    smartboard: {
        label: 'Smartboard',
        w: 200,
        h: 20,
        color: '#4A3F35',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    easel: {
        label: 'Easel',
        w: 70,
        h: 70,
        color: '#9A6045',
        seats: false,
        shape: 'triangle',
        group: 'fixtures',
    },
    cubbies: {
        label: 'Cubbies',
        w: 220,
        h: 40,
        color: '#D9A85C',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    coatHooks: {
        label: 'Coat hooks',
        w: 160,
        h: 18,
        color: '#D9A85C',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    sink: {
        label: 'Sink',
        w: 80,
        h: 60,
        color: '#8E9AAF',
        seats: false,
        shape: 'rounded',
        glyph: '◠',
        group: 'fixtures',
    },
    counter: {
        label: 'Counter',
        w: 220,
        h: 50,
        color: '#DCCDBB',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    clock: {
        label: 'Clock',
        w: 40,
        h: 40,
        color: '#6B5D52',
        seats: false,
        shape: 'circle',
        glyph: '◷',
        group: 'fixtures',
    },
    wordWall: {
        label: 'Word wall',
        w: 200,
        h: 18,
        color: '#9C7A93',
        seats: false,
        shape: 'rect',
        group: 'fixtures',
    },
    /* ----------------------------------------------------------- furniture */
    teacherDesk: {
        label: "Teacher's desk",
        w: 160,
        h: 80,
        color: '#9A6045',
        seats: false,
        shape: 'rounded',
        group: 'furniture',
    },
    teacherChair: {
        label: "Teacher's chair",
        w: 50,
        h: 50,
        color: '#9A6045',
        seats: false,
        shape: 'chair',
        group: 'furniture',
    },
    rockingChair: {
        label: 'Rocking chair',
        w: 60,
        h: 60,
        color: '#7C8A5F',
        seats: false,
        shape: 'chair',
        group: 'furniture',
    },
    rug: {
        label: 'Carpet',
        w: 260,
        h: 180,
        color: '#A3B18A',
        seats: false,
        shape: 'rounded',
        group: 'furniture',
    },
    roundRug: {
        label: 'Round carpet',
        w: 220,
        h: 220,
        color: '#A3B18A',
        seats: false,
        shape: 'circle',
        group: 'furniture',
    },
    shelf: {
        label: 'Bookshelf',
        w: 140,
        h: 40,
        color: '#7C8A5F',
        seats: false,
        shape: 'rect',
        group: 'furniture',
    },
    cabinet: {
        label: 'Cabinet',
        w: 120,
        h: 50,
        color: '#9C7A93',
        seats: false,
        shape: 'rect',
        group: 'furniture',
    },
    storageBins: {
        label: 'Storage bins',
        w: 120,
        h: 45,
        color: '#D9A85C',
        seats: false,
        shape: 'rect',
        group: 'furniture',
    },
    computer: {
        label: 'Computers',
        w: 150,
        h: 60,
        color: '#9C9086',
        seats: false,
        shape: 'rounded',
        group: 'furniture',
    },
    printer: {
        label: 'Printer',
        w: 60,
        h: 45,
        color: '#9C9086',
        seats: false,
        shape: 'rounded',
        glyph: '⎙',
        group: 'furniture',
    },
    cart: {
        label: 'Cart',
        w: 80,
        h: 50,
        color: '#8E9AAF',
        seats: false,
        shape: 'rounded',
        group: 'furniture',
    },
    lamp: {
        label: 'Lamp',
        w: 40,
        h: 40,
        color: '#D9A85C',
        seats: false,
        shape: 'triangle',
        group: 'furniture',
    },
    plant: {
        label: 'Plant',
        w: 50,
        h: 50,
        color: '#7C8A5F',
        seats: false,
        shape: 'circle',
        glyph: '❧',
        group: 'furniture',
    },
    fishTank: {
        label: 'Fish tank',
        w: 90,
        h: 50,
        color: '#8E9AAF',
        seats: false,
        shape: 'rounded',
        glyph: '≈',
        group: 'furniture',
    },
    calmCorner: {
        label: 'Calm corner',
        w: 160,
        h: 140,
        color: '#9C7A93',
        seats: false,
        shape: 'rounded',
        group: 'furniture',
    },
    mailboxes: {
        label: 'Mailboxes',
        w: 140,
        h: 45,
        color: '#C08B8B',
        seats: false,
        shape: 'rect',
        group: 'furniture',
    },
    label: {
        label: 'Label',
        w: 120,
        h: 34,
        color: '#EFE8DE',
        seats: false,
        shape: 'rect',
        group: 'furniture',
    },
};
/**
 * SVG paths in a 100×100 box, stretched to the item's footprint. Shapes not
 * listed here are drawn with plain CSS (rectangles, pills, circles).
 */
export const SHAPE_PATHS = {
    kidney: 'M4,42 C4,14 28,2 50,14 C68,24 84,20 93,34 C102,48 97,72 78,84 C57,97 38,88 27,74 C18,63 4,62 4,42 Z',
    trapezoid: 'M16,4 H84 L98,96 H2 Z',
    // A U, open towards the top, which is how a teacher sits inside one.
    horseshoe: 'M2,2 H30 V62 H70 V2 H98 V98 H2 Z',
    chair: 'M14,26 H86 V96 H14 Z M22,4 H78 V26 H22 Z',
    triangle: 'M50,4 L96,96 H4 Z',
    blob: 'M50,4 C78,4 96,24 96,50 C96,78 76,96 50,96 C24,96 4,78 4,50 C4,24 22,4 50,4 Z',
};
export const WALLS = ['top', 'right', 'bottom', 'left'];
export const DEFAULT_WALL_LABELS = {
    top: 'Front',
    right: 'Windows',
    bottom: 'Back',
    left: 'Door',
};
/** Keeps an item inside the room whatever its rotation. */
export function clampToRoom(item, room = ROOM) {
    const turned = item.rotation === 90 || item.rotation === 270;
    const w = turned ? item.h : item.w;
    const h = turned ? item.w : item.h;
    return {
        x: Math.min(Math.max(item.x, 0), Math.max(0, room.width - w)),
        y: Math.min(Math.max(item.y, 0), Math.max(0, room.height - h)),
    };
}
export function snap(value) {
    return Math.round(value / ROOM.grid) * ROOM.grid;
}
export function itemLabel(item) {
    return item.label?.trim() || ROOM_ITEM_SPECS[item.kind].label;
}
export function isSeat(item) {
    return ROOM_ITEM_SPECS[item.kind].seats;
}
/**
 * A starting room: desks in five rows of five, teacher's desk at the front,
 * a door on the left wall and a whiteboard across the front.
 *
 * Twenty-five is a guess at a class size, not a constraint — desks are added
 * and removed like anything else.
 */
export function defaultRoomItems(deskCount = 25) {
    const items = [];
    const spec = ROOM_ITEM_SPECS.desk;
    const perRow = 5;
    const gapX = 40;
    const gapY = 55;
    const startX = Math.round((ROOM.width - (perRow * spec.w + (perRow - 1) * gapX)) / 2);
    const startY = 250;
    for (let i = 0; i < deskCount; i += 1) {
        const row = Math.floor(i / perRow);
        const col = i % perRow;
        items.push({
            id: `desk-${i + 1}`,
            kind: 'desk',
            x: snap(startX + col * (spec.w + gapX)),
            y: snap(startY + row * (spec.h + gapY)),
            w: spec.w,
            h: spec.h,
            rotation: 0,
            label: null,
            studentId: null,
        });
    }
    items.push({
        id: 'whiteboard-1',
        kind: 'whiteboard',
        x: snap((ROOM.width - ROOM_ITEM_SPECS.whiteboard.w) / 2),
        y: 50,
        w: ROOM_ITEM_SPECS.whiteboard.w,
        h: ROOM_ITEM_SPECS.whiteboard.h,
        rotation: 0,
        label: null,
        studentId: null,
    }, {
        id: 'teacher-1',
        kind: 'teacherDesk',
        x: 120,
        y: 100,
        w: ROOM_ITEM_SPECS.teacherDesk.w,
        h: ROOM_ITEM_SPECS.teacherDesk.h,
        rotation: 0,
        label: null,
        studentId: null,
    }, {
        id: 'door-1',
        kind: 'door',
        x: 0,
        y: 700,
        w: ROOM_ITEM_SPECS.door.w,
        h: ROOM_ITEM_SPECS.door.h,
        rotation: 90,
        label: null,
        studentId: null,
    });
    return items;
}
/* ---------------------------------------------------------- seating rules */
/**
 * Rules a seating chart should respect.
 *
 * Deliberately a short list. A teacher can express "these two can't sit
 * together" and "this child needs to be near the board" in seconds, and those
 * two cover most of what an IEP or a rough week actually demands. Anything
 * more expressive would be a query language nobody wants to write at 7am.
 *
 * Rules describe intent, not enforcement: a violated rule is flagged, never
 * blocked. The teacher knows things the app doesn't, and seating a child badly
 * on purpose for one afternoon is their call to make.
 */
export const RULE_KINDS = ['apart', 'together', 'nearItem', 'awayFromItem'];
export const RULE_KIND_LABELS = {
    apart: 'Keep apart',
    together: 'Seat together',
    nearItem: 'Seat near',
    awayFromItem: 'Seat away from',
};
export const RULE_KIND_HINTS = {
    apart: 'Two children who should not sit near each other.',
    together: 'A pairing that helps — a reading buddy, or a translator.',
    nearItem: 'Near the board, the teacher, or anything else in the room.',
    awayFromItem: 'Away from the door, a window, or the noisy corner.',
};
/** Sensible starting distances, in feet, when a rule is measured that way. */
export const RULE_DEFAULT_FEET = {
    apart: 8,
    together: 5,
    nearItem: 10,
    awayFromItem: 8,
};
/**
 * How a rule is measured.
 *
 * 'adjacent' is the one teachers reach for first — "those two can't sit next
 * to each other" is about the seats touching, not about a distance in feet.
 * Feet are still available for the cases that need them, like keeping a child
 * within ten feet of the board.
 */
export const RULE_MODES = ['adjacent', 'feet'];
/**
 * Slack allowed when deciding whether two seats are side by side, in feet.
 *
 * Desks in a row are usually pushed together or nearly so; anything within
 * about a foot and a half counts as beside. Diagonal neighbours count too —
 * a child can reach across a corner just as easily.
 */
export const ADJACENT_SLACK_FEET = 1.5;
/** Do two items sit side by side, corner to corner, or touching? */
export function areAdjacent(a, b) {
    const slack = ADJACENT_SLACK_FEET * UNITS_PER_FOOT;
    const boxOf = (item) => {
        const turned = item.rotation === 90 || item.rotation === 270;
        const w = turned ? item.h : item.w;
        const h = turned ? item.w : item.h;
        return { left: item.x, right: item.x + w, top: item.y, bottom: item.y + h };
    };
    const p = boxOf(a);
    const q = boxOf(b);
    // Grow both boxes by the slack and see whether they overlap.
    const gapX = Math.max(p.left - q.right, q.left - p.right);
    const gapY = Math.max(p.top - q.bottom, q.top - p.bottom);
    return gapX <= slack && gapY <= slack;
}
/** Centre of an item, accounting for rotation. */
export function itemCenter(item) {
    const turned = item.rotation === 90 || item.rotation === 270;
    const w = turned ? item.h : item.w;
    const h = turned ? item.w : item.h;
    return { x: item.x + w / 2, y: item.y + h / 2 };
}
/** Straight-line distance between two items, in feet. */
export function feetBetween(a, b) {
    const p = itemCenter(a);
    const q = itemCenter(b);
    return Math.hypot(p.x - q.x, p.y - q.y) / UNITS_PER_FOOT;
}
function seatOf(items, studentId) {
    if (!studentId)
        return undefined;
    return items.find((i) => i.studentId === studentId);
}
/**
 * Which rules the current seating breaks.
 *
 * A rule about a child who isn't seated yet is not a violation — it is simply
 * not applicable, and reporting it would bury the real problems under noise
 * every time the chart is cleared.
 */
export function findViolations(rules, items, nameOf) {
    const out = [];
    for (const rule of rules) {
        if (!rule.active)
            continue;
        const seat = seatOf(items, rule.studentId);
        if (!seat)
            continue;
        const who = nameOf(rule.studentId);
        if (rule.kind === 'apart' || rule.kind === 'together') {
            const otherSeat = seatOf(items, rule.otherStudentId);
            if (!otherSeat)
                continue;
            const other = nameOf(rule.otherStudentId);
            const beside = areAdjacent(seat, otherSeat);
            const gap = feetBetween(seat, otherSeat);
            if (rule.mode === 'adjacent') {
                if (rule.kind === 'apart' && beside) {
                    out.push({
                        ruleId: rule.id,
                        itemIds: [seat.id, otherSeat.id],
                        message: `${who} and ${other} are sitting next to each other.`,
                    });
                }
                if (rule.kind === 'together' && !beside) {
                    out.push({
                        ruleId: rule.id,
                        itemIds: [seat.id, otherSeat.id],
                        message: `${who} and ${other} are not sitting together.`,
                    });
                }
                continue;
            }
            if (rule.kind === 'apart' && gap < rule.feet) {
                out.push({
                    ruleId: rule.id,
                    itemIds: [seat.id, otherSeat.id],
                    message: `${who} and ${other} are ${Math.round(gap)} ft apart — should be at least ${rule.feet}.`,
                });
            }
            if (rule.kind === 'together' && gap > rule.feet) {
                out.push({
                    ruleId: rule.id,
                    itemIds: [seat.id, otherSeat.id],
                    message: `${who} and ${other} are ${Math.round(gap)} ft apart — should be within ${rule.feet}.`,
                });
            }
            continue;
        }
        const target = items.find((i) => i.id === rule.itemId);
        // The furniture may have been removed since the rule was written; the rule
        // stays, and simply has nothing to measure against until it is re-pointed.
        if (!target)
            continue;
        const gap = feetBetween(seat, target);
        const what = itemLabel(target).toLowerCase();
        if (rule.mode === 'adjacent') {
            const beside = areAdjacent(seat, target);
            if (rule.kind === 'nearItem' && !beside) {
                out.push({
                    ruleId: rule.id,
                    itemIds: [seat.id, target.id],
                    message: `${who} is not right beside the ${what}.`,
                });
            }
            if (rule.kind === 'awayFromItem' && beside) {
                out.push({
                    ruleId: rule.id,
                    itemIds: [seat.id, target.id],
                    message: `${who} is right beside the ${what}.`,
                });
            }
            continue;
        }
        if (rule.kind === 'nearItem' && gap > rule.feet) {
            out.push({
                ruleId: rule.id,
                itemIds: [seat.id, target.id],
                message: `${who} is ${Math.round(gap)} ft from the ${what} — should be within ${rule.feet}.`,
            });
        }
        if (rule.kind === 'awayFromItem' && gap < rule.feet) {
            out.push({
                ruleId: rule.id,
                itemIds: [seat.id, target.id],
                message: `${who} is ${Math.round(gap)} ft from the ${what} — should be at least ${rule.feet}.`,
            });
        }
    }
    return out;
}
/** A short human summary of a rule, for the list. */
export function ruleSummary(rule, nameOf, items) {
    const who = nameOf(rule.studentId);
    const adjacent = rule.mode === 'adjacent';
    if (rule.kind === 'apart' || rule.kind === 'together') {
        const other = rule.otherStudentId ? nameOf(rule.otherStudentId) : 'someone';
        if (rule.kind === 'apart') {
            return adjacent
                ? `${who} and ${other} — never side by side`
                : `${who} and ${other} — at least ${rule.feet} ft apart`;
        }
        return adjacent
            ? `${who} and ${other} — side by side`
            : `${who} and ${other} — within ${rule.feet} ft`;
    }
    const target = items.find((i) => i.id === rule.itemId);
    const what = target ? itemLabel(target).toLowerCase() : 'something no longer in the room';
    if (rule.kind === 'nearItem') {
        return adjacent
            ? `${who} — right beside the ${what}`
            : `${who} — within ${rule.feet} ft of the ${what}`;
    }
    return adjacent
        ? `${who} — never beside the ${what}`
        : `${who} — at least ${rule.feet} ft from the ${what}`;
}
/**
 * Score a seat for a child: lower is better, Infinity means a rule forbids it.
 *
 * Used when filling empty seats. Hard constraints (keep apart, stay away from)
 * rule a seat out; soft ones (sit together, sit near) push the child towards
 * the seats that satisfy them rather than forbidding the rest, because a
 * classroom rarely has enough seats to satisfy everything.
 */
export function scoreSeat(studentId, seat, rules, items) {
    let score = 0;
    for (const rule of rules) {
        if (!rule.active)
            continue;
        const involves = rule.studentId === studentId ||
            (rule.otherStudentId === studentId && (rule.kind === 'apart' || rule.kind === 'together'));
        if (!involves)
            continue;
        if (rule.kind === 'apart' || rule.kind === 'together') {
            const otherId = rule.studentId === studentId ? rule.otherStudentId : rule.studentId;
            const otherSeat = seatOf(items, otherId ?? null);
            if (!otherSeat || otherSeat.id === seat.id)
                continue;
            if (rule.mode === 'adjacent') {
                const beside = areAdjacent(seat, otherSeat);
                if (rule.kind === 'apart' && beside)
                    return Infinity;
                if (rule.kind === 'together' && !beside)
                    score += feetBetween(seat, otherSeat);
                continue;
            }
            const gap = feetBetween(seat, otherSeat);
            if (rule.kind === 'apart') {
                if (gap < rule.feet)
                    return Infinity;
            }
            else if (gap > rule.feet) {
                score += gap - rule.feet;
            }
            continue;
        }
        const target = items.find((i) => i.id === rule.itemId);
        if (!target)
            continue;
        if (rule.mode === 'adjacent') {
            const beside = areAdjacent(seat, target);
            if (rule.kind === 'awayFromItem' && beside)
                return Infinity;
            if (rule.kind === 'nearItem' && !beside)
                score += feetBetween(seat, target);
            continue;
        }
        const gap = feetBetween(seat, target);
        if (rule.kind === 'awayFromItem' && gap < rule.feet)
            return Infinity;
        if (rule.kind === 'nearItem' && gap > rule.feet)
            score += gap - rule.feet;
    }
    return score;
}
/**
 * Fill empty seats, respecting the rules as far as the room allows.
 *
 * Children with the most rules attached are placed first — they have the
 * fewest workable seats, and leaving them until last is how you end up with
 * one child and one seat that breaks a rule. Anyone who cannot be placed
 * without breaking a hard rule is handed back unplaced rather than seated
 * somewhere the teacher explicitly forbade.
 */
export function planSeating(studentIds, items, rules) {
    const active = rules.filter((r) => r.active);
    const ruleCount = (id) => active.filter((r) => r.studentId === id || r.otherStudentId === id).length;
    const queue = [...studentIds].sort((a, b) => ruleCount(b) - ruleCount(a));
    let working = items.map((i) => ({ ...i }));
    const seating = {};
    const unplaced = [];
    for (const studentId of queue) {
        const open = working.filter((i) => isSeat(i) && !i.studentId);
        let best = null;
        for (const seat of open) {
            const score = scoreSeat(studentId, seat, active, working);
            if (score === Infinity)
                continue;
            if (!best || score < best.score)
                best = { seat, score };
            if (score === 0)
                break;
        }
        if (!best) {
            unplaced.push(studentId);
            continue;
        }
        seating[studentId] = best.seat.id;
        working = working.map((i) => (i.id === best.seat.id ? { ...i, studentId } : i));
    }
    return { seating, unplaced };
}
/* ------------------------------------------------------------ assessments */
export const TERMS = ['BOY', 'MOY', 'EOY'];
export const TERM_LABELS = {
    BOY: 'Beginning of year',
    MOY: 'Middle of year',
    EOY: 'End of year',
};
export const SCORE_TYPES = ['number', 'percent', 'level'];
export const DEFAULT_BANDS = [
    { label: 'Well below', min: 0, color: '#9A6045' },
    { label: 'Below', min: 60, color: '#D9A85C' },
    { label: 'At benchmark', min: 80, color: '#A3B18A' },
    { label: 'Above', min: 90, color: '#7C8A5F' },
];
/** The band a score falls into, or null if it is outside every band. */
export function bandFor(value, bands) {
    if (value == null || bands.length === 0)
        return null;
    const sorted = [...bands].sort((a, b) => a.min - b.min);
    let found = null;
    for (const band of sorted) {
        if (value >= band.min)
            found = band;
    }
    return found;
}
export function scoreLabel(score, measure) {
    if (!score)
        return '';
    if (measure.scoreType === 'level')
        return score.level ?? '';
    if (score.value == null)
        return '';
    if (measure.scoreType === 'percent' && measure.outOf) {
        return `${score.value}/${measure.outOf}`;
    }
    return String(score.value);
}
/** Percent-based measures are banded on the percentage, not the raw count. */
export function bandValue(score, measure) {
    if (!score || score.value == null)
        return null;
    if (measure.scoreType === 'percent' && measure.outOf) {
        return Math.round((score.value / measure.outOf) * 100);
    }
    return score.value;
}
/** How many students sit in each band, for the summary strip above a grid. */
export function bandCounts(scores, measure) {
    const counts = new Map();
    for (const score of scores) {
        const band = bandFor(bandValue(score, measure), measure.bands);
        if (band)
            counts.set(band.label, (counts.get(band.label) ?? 0) + 1);
    }
    return [...measure.bands]
        .sort((a, b) => a.min - b.min)
        .map((band) => ({ band, count: counts.get(band.label) ?? 0 }));
}
/**
 * Growth between two terms, for the BOY/MOY/EOY view.
 *
 * Null when either end is missing — a child who was absent in the autumn has
 * no growth to report, and showing their EOY score as the gain would be a lie
 * that flatters everyone.
 */
export function growth(from, to) {
    if (from?.value == null || to?.value == null)
        return null;
    return to.value - from.value;
}
/**
 * Templates in the order they are worth offering to this teacher.
 *
 * Hers first, in their shipped order; everything else after, still in its
 * shipped order. `matches` says which side of the line each one fell, so the
 * picker can draw the line rather than silently reordering a list.
 */
export function templatesForGrade(templates, grade) {
    const withMatch = templates.map((template) => ({
        template,
        // No grade set, or a template that names none, matches everybody: an
        // untagged template is "any grade", not "no grade".
        matches: !grade || template.grades.length === 0 || template.grades.includes(grade),
    }));
    return [...withMatch.filter((t) => t.matches), ...withMatch.filter((t) => !t.matches)];
}
const PERCENT_BANDS = [
    { label: 'Well below', min: 0, color: '#9A6045' },
    { label: 'Approaching', min: 60, color: '#D9A85C' },
    { label: 'Proficient', min: 80, color: '#A3B18A' },
];
/**
 * Ready-made setups for the assessments Elkhart actually gives.
 *
 * The measures and units are real; the benchmark cut scores are NOT filled in
 * with official numbers. Publisher cut scores change by grade, by term and by
 * revision, and a wrong number quietly baked into an app would put a child in
 * the wrong band with no visible reason. Percent-based work gets the
 * conventional 60/80 split, everything else starts unbanded for the teacher
 * to fill from their own materials.
 */
export const ASSESSMENT_TEMPLATES = [
    {
        name: 'NWEA MAP — Reading',
        subject: 'Reading',
        termly: true,
        grades: ['K', '1', '2', '3', '4', '5', '6', '7', '8'],
        blurb: 'RIT score and percentile, three times a year.',
        measures: [
            { name: 'Reading RIT', scoreType: 'number', outOf: null, unit: 'RIT', bands: [] },
            { name: 'Percentile', scoreType: 'number', outOf: null, unit: '%ile', bands: [] },
        ],
    },
    {
        name: 'NWEA MAP — Math',
        subject: 'Math',
        termly: true,
        grades: ['K', '1', '2', '3', '4', '5', '6', '7', '8'],
        blurb: 'RIT score and percentile, three times a year.',
        measures: [
            { name: 'Math RIT', scoreType: 'number', outOf: null, unit: 'RIT', bands: [] },
            { name: 'Percentile', scoreType: 'number', outOf: null, unit: '%ile', bands: [] },
        ],
    },
    {
        name: 'Acadience Reading',
        subject: 'Reading',
        termly: true,
        // The sub-measures below are the first-grade set; other grades use a
        // different mix, which is why this is tagged to grade 1 alone.
        grades: ['1'],
        blurb: 'Composite plus the first-grade sub-measures. Add your own cut scores.',
        measures: [
            { name: 'Composite', scoreType: 'number', outOf: null, unit: 'composite', bands: [] },
            {
                name: 'NWF — correct letter sounds',
                scoreType: 'number',
                outOf: null,
                unit: 'CLS',
                bands: [],
            },
            { name: 'NWF — whole words read', scoreType: 'number', outOf: null, unit: 'WWR', bands: [] },
            { name: 'ORF — words correct', scoreType: 'number', outOf: null, unit: 'wcpm', bands: [] },
            { name: 'ORF — accuracy', scoreType: 'percent', outOf: 100, unit: '%', bands: PERCENT_BANDS },
            { name: 'Retell', scoreType: 'number', outOf: null, unit: 'words', bands: [] },
        ],
    },
    {
        name: 'CKLA Skills — unit assessment',
        subject: 'Reading',
        termly: false,
        grades: ['K', '1', '2', '3', '4', '5'],
        blurb: 'One column per unit, scored out of a total you set.',
        measures: [
            {
                name: 'Unit assessment',
                scoreType: 'percent',
                outOf: 20,
                unit: 'correct',
                bands: PERCENT_BANDS,
            },
        ],
    },
    {
        name: 'CKLA Knowledge — domain assessment',
        subject: 'Reading',
        termly: false,
        grades: ['K', '1', '2', '3', '4', '5'],
        blurb: 'Domain assessments, scored out of a total you set.',
        measures: [
            {
                name: 'Domain assessment',
                scoreType: 'percent',
                outOf: 10,
                unit: 'correct',
                bands: PERCENT_BANDS,
            },
        ],
    },
    /**
     * The CORE Phonics Survey.
     *
     * Sections rather than one number, because that is the entire point of it:
     * a child at 40% overall could be fine on letter sounds and lost on vowel
     * teams, and those are two different Tuesdays of teaching. One measure per
     * section is what makes the grid answer "who needs digraphs" at a glance.
     *
     * The totals are the ones on the survey's own record sheet. The bands are
     * the conventional 60/80 split rather than published cut scores — CORE
     * gives its guidance in words rather than fixed numbers, and inventing a
     * boundary here would put a child in a band for a reason nobody could see.
     * The percentages are hers to move.
     */
    {
        name: 'CORE Phonics Survey',
        subject: 'Reading',
        termly: true,
        grades: ['K', '1', '2', '3'],
        blurb: 'Every section of the survey, scored separately. A first grader normally reaches r-controlled and long vowels; the later sections are there for whoever goes further.',
        measures: [
            { name: 'Letter names — upper', scoreType: 'percent', outOf: 26, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Letter names — lower', scoreType: 'percent', outOf: 26, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Consonant sounds', scoreType: 'percent', outOf: 23, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Long vowel sounds', scoreType: 'percent', outOf: 5, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Short vowel sounds', scoreType: 'percent', outOf: 5, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Short vowels in CVC', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Consonant blends with short vowels', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Short vowels, digraphs and -tch', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'R-controlled vowels', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Long vowel spellings', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Variant vowels', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Low-frequency spellings', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
            { name: 'Multisyllabic words', scoreType: 'percent', outOf: 15, unit: 'correct', bands: PERCENT_BANDS },
        ],
    },
    {
        name: 'District assessment',
        subject: 'Other',
        termly: false,
        // Any grade: a district assessment is whatever the district says it is.
        grades: [],
        blurb: 'An empty one to shape however your district wants it.',
        measures: [
            { name: 'Score', scoreType: 'percent', outOf: 100, unit: '%', bands: PERCENT_BANDS },
        ],
    },
];
/**
 * Parse a block pasted from a spreadsheet.
 *
 * Excel and Sheets both put tabs between cells and newlines between rows, so
 * this handles either, and tolerates the trailing newline every spreadsheet
 * adds. Blank cells stay blank rather than becoming zero — an empty cell means
 * "not assessed", and zero means "scored nothing", which are not the same
 * thing about a child.
 */
export function parsePastedGrid(text) {
    return text
        .replace(/\r\n?/g, '\n')
        .replace(/\n+$/, '')
        .split('\n')
        .map((line) => line.split('\t').map((cell) => cell.trim()));
}
/** A pasted cell to a number, or null when it isn't one. */
export function parseScoreCell(cell) {
    if (!cell)
        return null;
    const cleaned = cell.replace(/[%,]/g, '').trim();
    if (!cleaned)
        return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}
export const FONT_CATEGORY_LABELS = {
    friendly: 'Warm and friendly',
    bold: 'Bold and confident',
    classic: 'Classic and bookish',
    handwritten: 'Handwritten',
    playful: 'Fun and playful',
    plain: 'Plain and precise',
};
/**
 * The fonts on offer.
 *
 * All from Google Fonts, all free to use, and all loaded only when actually
 * chosen — a list this long would be several megabytes if every one were
 * fetched up front, so the picker loads them on demand and the app loads
 * exactly one.
 */
export const FONTS = [
    // Warm and friendly
    {
        id: 'nunito',
        name: 'Nunito Sans',
        category: 'friendly',
        stack: "'Nunito Sans', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'quicksand',
        name: 'Quicksand',
        category: 'friendly',
        stack: "'Quicksand', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'poppins',
        name: 'Poppins',
        category: 'friendly',
        stack: "'Poppins', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'rubik',
        name: 'Rubik',
        category: 'friendly',
        stack: "'Rubik', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'karla',
        name: 'Karla',
        category: 'friendly',
        stack: "'Karla', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'mulish',
        name: 'Mulish',
        category: 'friendly',
        stack: "'Mulish', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'varela',
        name: 'Varela Round',
        category: 'friendly',
        stack: "'Varela Round', sans-serif",
        weights: '400',
    },
    {
        id: 'comfortaa',
        name: 'Comfortaa',
        category: 'friendly',
        stack: "'Comfortaa', cursive",
        weights: '400;600;700',
    },
    // Bold and confident
    {
        id: 'montserrat',
        name: 'Montserrat',
        category: 'bold',
        stack: "'Montserrat', sans-serif",
        weights: '400;600;800',
    },
    {
        id: 'raleway',
        name: 'Raleway',
        category: 'bold',
        stack: "'Raleway', sans-serif",
        weights: '400;600;800',
    },
    {
        id: 'oswald',
        name: 'Oswald',
        category: 'bold',
        stack: "'Oswald', sans-serif",
        weights: '400;600',
    },
    {
        id: 'archivo',
        name: 'Archivo Black',
        category: 'bold',
        stack: "'Archivo Black', sans-serif",
        weights: '400',
    },
    { id: 'anton', name: 'Anton', category: 'bold', stack: "'Anton', sans-serif", weights: '400' },
    {
        id: 'bebas',
        name: 'Bebas Neue',
        category: 'bold',
        stack: "'Bebas Neue', sans-serif",
        weights: '400',
    },
    {
        id: 'righteous',
        name: 'Righteous',
        category: 'bold',
        stack: "'Righteous', cursive",
        weights: '400',
    },
    {
        id: 'alfa',
        name: 'Alfa Slab One',
        category: 'bold',
        stack: "'Alfa Slab One', serif",
        weights: '400',
    },
    // Classic and bookish
    {
        id: 'fraunces',
        name: 'Fraunces',
        category: 'classic',
        stack: "'Fraunces', serif",
        weights: '400;600;700',
    },
    { id: 'lora', name: 'Lora', category: 'classic', stack: "'Lora', serif", weights: '400;600;700' },
    {
        id: 'merriweather',
        name: 'Merriweather',
        category: 'classic',
        stack: "'Merriweather', serif",
        weights: '400;700',
    },
    {
        id: 'playfair',
        name: 'Playfair Display',
        category: 'classic',
        stack: "'Playfair Display', serif",
        weights: '400;600;700',
    },
    {
        id: 'crimson',
        name: 'Crimson Pro',
        category: 'classic',
        stack: "'Crimson Pro', serif",
        weights: '400;600;700',
    },
    {
        id: 'bitter',
        name: 'Bitter',
        category: 'classic',
        stack: "'Bitter', serif",
        weights: '400;600;700',
    },
    {
        id: 'eb-garamond',
        name: 'EB Garamond',
        category: 'classic',
        stack: "'EB Garamond', serif",
        weights: '400;600;700',
    },
    {
        id: 'libre-baskerville',
        name: 'Libre Baskerville',
        category: 'classic',
        stack: "'Libre Baskerville', serif",
        weights: '400;700',
    },
    // Handwritten
    {
        id: 'caveat',
        name: 'Caveat',
        category: 'handwritten',
        stack: "'Caveat', cursive",
        weights: '400;600;700',
    },
    {
        id: 'kalam',
        name: 'Kalam',
        category: 'handwritten',
        stack: "'Kalam', cursive",
        weights: '400;700',
    },
    {
        id: 'patrick',
        name: 'Patrick Hand',
        category: 'handwritten',
        stack: "'Patrick Hand', cursive",
        weights: '400',
    },
    {
        id: 'shadows',
        name: 'Shadows Into Light',
        category: 'handwritten',
        stack: "'Shadows Into Light', cursive",
        weights: '400',
    },
    {
        id: 'indie',
        name: 'Indie Flower',
        category: 'handwritten',
        stack: "'Indie Flower', cursive",
        weights: '400',
    },
    {
        id: 'gloria',
        name: 'Gloria Hallelujah',
        category: 'handwritten',
        stack: "'Gloria Hallelujah', cursive",
        weights: '400',
    },
    {
        id: 'architects',
        name: 'Architects Daughter',
        category: 'handwritten',
        stack: "'Architects Daughter', cursive",
        weights: '400',
    },
    {
        id: 'nanum-pen',
        name: 'Nanum Pen Script',
        category: 'handwritten',
        stack: "'Nanum Pen Script', cursive",
        weights: '400',
    },
    // Fun and playful
    {
        id: 'fredoka',
        name: 'Fredoka',
        category: 'playful',
        stack: "'Fredoka', sans-serif",
        weights: '400;600',
    },
    {
        id: 'baloo',
        name: 'Baloo 2',
        category: 'playful',
        stack: "'Baloo 2', cursive",
        weights: '400;600;700',
    },
    { id: 'chewy', name: 'Chewy', category: 'playful', stack: "'Chewy', cursive", weights: '400' },
    {
        id: 'bubblegum',
        name: 'Bubblegum Sans',
        category: 'playful',
        stack: "'Bubblegum Sans', cursive",
        weights: '400',
    },
    {
        id: 'lilita',
        name: 'Lilita One',
        category: 'playful',
        stack: "'Lilita One', cursive",
        weights: '400',
    },
    {
        id: 'luckiest',
        name: 'Luckiest Guy',
        category: 'playful',
        stack: "'Luckiest Guy', cursive",
        weights: '400',
    },
    {
        id: 'schoolbell',
        name: 'Schoolbell',
        category: 'playful',
        stack: "'Schoolbell', cursive",
        weights: '400',
    },
    {
        id: 'sniglet',
        name: 'Sniglet',
        category: 'playful',
        stack: "'Sniglet', cursive",
        weights: '400;800',
    },
    {
        id: 'grandstander',
        name: 'Grandstander',
        category: 'playful',
        stack: "'Grandstander', cursive",
        weights: '400;600;700',
    },
    // Plain and precise
    {
        id: 'inter',
        name: 'Inter',
        category: 'plain',
        stack: "'Inter', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'work-sans',
        name: 'Work Sans',
        category: 'plain',
        stack: "'Work Sans', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'source-sans',
        name: 'Source Sans 3',
        category: 'plain',
        stack: "'Source Sans 3', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'ibm-plex',
        name: 'IBM Plex Sans',
        category: 'plain',
        stack: "'IBM Plex Sans', sans-serif",
        weights: '400;600;700',
    },
    {
        id: 'atkinson',
        name: 'Atkinson Hyperlegible',
        category: 'plain',
        stack: "'Atkinson Hyperlegible', sans-serif",
        weights: '400;700',
    },
    {
        id: 'space-mono',
        name: 'Space Mono',
        category: 'plain',
        stack: "'Space Mono', monospace",
        weights: '400;700',
    },
    {
        id: 'jetbrains',
        name: 'JetBrains Mono',
        category: 'plain',
        stack: "'JetBrains Mono', monospace",
        weights: '400;700',
    },
];
export const DEFAULT_BODY_FONT = 'nunito';
export const DEFAULT_HEADING_FONT = 'fraunces';
export function fontById(id) {
    return FONTS.find((f) => f.id === id) ?? null;
}
/** The Google Fonts URL for a set of choices, or null if there is nothing to load. */
export function googleFontsUrl(ids) {
    const families = [...new Set(ids.map(fontById).filter((f) => f !== null))]
        .map((f) => `family=${f.name.replace(/ /g, '+')}:wght@${f.weights}`)
        .join('&');
    return families ? `https://fonts.googleapis.com/css2?${families}&display=swap` : null;
}
//# sourceMappingURL=index.js.map